// Centralised theme + font-size helpers for the admin app.
//
// Theme model: 'light' | 'dark' | 'auto'.
//   - 'light' / 'dark' force that scheme.
//   - 'auto' tracks the OS via prefers-color-scheme.
//
// The data-theme attribute on <html> is what CSS reads; we never persist
// 'auto' as the attribute value (we resolve it to light/dark first).
//
// Font size: 'S' | 'M' | 'L', persisted as data-font-size on <html>.

export type Theme = 'light' | 'dark' | 'auto';
export type FontSize = 'S' | 'M' | 'L';

export const VALID_THEMES: readonly Theme[] = ['light', 'dark', 'auto'];
export const VALID_FONT_SIZES: readonly FontSize[] = ['S', 'M', 'L'];
// Default to 'auto' so a fresh visitor on plain web (no stored preference)
// gets the OS-level theme, matching how Telegram WebApp resolves theme.
// The pre-paint script in index.html falls back to prefers-color-scheme
// on its own; this keeps the React-side source-of-truth in agreement.
export const DEFAULT_THEME: Theme = 'auto';
export const DEFAULT_FONT_SIZE: FontSize = 'M';

const THEME_KEY = 'mnp.theme';
const FONT_SIZE_KEY = 'mnp.fontSize';

export function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (!stored) return DEFAULT_THEME;
    if ((VALID_THEMES as readonly string[]).includes(stored)) return stored as Theme;
    console.warn(`[mnp] Invalid theme "${stored}" in localStorage, falling back to "${DEFAULT_THEME}"`);
    return DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function readStoredFontSize(): FontSize {
  try {
    const stored = localStorage.getItem(FONT_SIZE_KEY);
    if (!stored) return DEFAULT_FONT_SIZE;
    if ((VALID_FONT_SIZES as readonly string[]).includes(stored)) return stored as FontSize;
    console.warn(`[mnp] Invalid font size "${stored}" in localStorage, falling back to "${DEFAULT_FONT_SIZE}"`);
    return DEFAULT_FONT_SIZE;
  } catch {
    return DEFAULT_FONT_SIZE;
  }
}

export function persistTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

export function persistFontSize(fontSize: FontSize): void {
  try {
    localStorage.setItem(FONT_SIZE_KEY, fontSize);
  } catch {
    /* ignore */
  }
}

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Resolve a Theme (incl. 'auto') down to the concrete attribute value. */
export function resolveThemeAttr(theme: Theme): 'light' | 'dark' {
  if (theme === 'auto') return prefersDark() ? 'dark' : 'light';
  return theme;
}

export function applyThemeAttr(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', resolveThemeAttr(theme));
}

export function applyFontSizeAttr(fontSize: FontSize): void {
  document.documentElement.setAttribute('data-font-size', fontSize);
}

let autoListenerAttached = false;
let autoListenerHandler: ((e: MediaQueryListEvent) => void) | null = null;
let autoListenerMq: MediaQueryList | null = null;

/**
 * Apply the stored theme + font-size at app start, and (when theme is 'auto')
 * keep the attribute in sync with OS changes. Idempotent.
 */
export function applyStoredAppearance(): void {
  const theme = readStoredTheme();
  const fontSize = readStoredFontSize();
  applyThemeAttr(theme);
  applyFontSizeAttr(fontSize);
  syncAutoListener(theme);
}

function syncAutoListener(theme: Theme): void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

  // Detach any existing listener first.
  if (autoListenerAttached && autoListenerMq && autoListenerHandler) {
    if (typeof autoListenerMq.removeEventListener === 'function') {
      autoListenerMq.removeEventListener('change', autoListenerHandler);
    } else if (typeof (autoListenerMq as any).removeListener === 'function') {
      (autoListenerMq as any).removeListener(autoListenerHandler);
    }
    autoListenerAttached = false;
    autoListenerHandler = null;
    autoListenerMq = null;
  }

  if (theme !== 'auto') return;

  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e: MediaQueryListEvent) => {
    document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
  };
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', handler);
  } else if (typeof (mq as any).addListener === 'function') {
    (mq as any).addListener(handler);
  }
  autoListenerAttached = true;
  autoListenerHandler = handler;
  autoListenerMq = mq;
}

/** Called when the user changes the theme in Settings. */
export function setTheme(theme: Theme): void {
  persistTheme(theme);
  applyThemeAttr(theme);
  syncAutoListener(theme);
}

export function setFontSize(fontSize: FontSize): void {
  persistFontSize(fontSize);
  applyFontSizeAttr(fontSize);
}
